// Types
import type {
  EmployeeTraining,
  TrainingHistory,
  EmployeeSupporter,
  EmployeeTrainingActivity,
} from '@/types/employee';
import { ActivityType } from '@shared/types';

// Constants
import { TrainingFieldIdType } from '@/constants/employee';

export enum Sentiment {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
  NEUTRAL = 'neutral',
}

export type TrainingCardData = Pick<
  EmployeeTraining,
  'topicLabel' | 'user' | 'start' | 'estEnd' | 'actualEnd'
> & {
  status?: string;
  supporters?: Partial<EmployeeSupporter>[];
  histories?: Partial<TrainingHistory>[];
  updatedAt?: string;
};

// Training field type for editing (description, presentation, or feedback)
export type TrainingFieldType =
  | ActivityType.TRAINING_UPDATE
  | ActivityType.TRAINING_PRESENTATION
  | ActivityType.TRAINING_FEEDBACK_RECEIVED
  | ActivityType.TRAINING_FEEDBACK_PRESENTATION
  | ActivityType.TRAINING_UPDATE_FEEDBACK
  | ActivityType.TRAINING_UPDATE_FEEDBACK_PRESENTATION;

// Options for training field ID generation
export type TrainingFieldIdOptions =
  | {
      type: TrainingFieldIdType.DESCRIPTION;
    }
  | {
      type: TrainingFieldIdType.PRESENTATION;
      activityId: string;
    }
  | {
      type: TrainingFieldIdType.FEEDBACK;
      activityId: string;
    }
  | {
      type: TrainingFieldIdType.CREATE_FEEDBACK;
    }
  | {
      type: TrainingFieldIdType.CREATE_PRESENTATION;
    };

// Training action payload types for chat actions
export interface TrainingPresentationPayload {
  identity: string;
  type: ActivityType.TRAINING_UPDATE_PRESENTATION;
  data: {
    presentationContent: string;
    activityId: string;
  };
}

export interface TrainingFeedbackPayload {
  identity: string;
  type: ActivityType.TRAINING_FEEDBACK_RECEIVED;
  data: {
    feedback: string;
  };
}

export interface TrainingDescPayload {
  identity: string;
  type: ActivityType.TRAINING_UPDATE;
  data: {
    desc: string;
  };
}

export interface TrainingFeedbackPresentationPayload {
  identity: string;
  type: ActivityType.TRAINING_FEEDBACK_PRESENTATION;
  data: {
    activityId: string;
    feedback: string;
  };
}

export interface TrainingUpdateFeedbackPayload {
  identity: string;
  type: ActivityType.TRAINING_UPDATE_FEEDBACK;
  data: {
    activityId: string;
    feedback: string;
  };
}

export interface TrainingCreatePresentationPayload {
  identity: string;
  type: ActivityType.TRAINING_PRESENTATION;
  data: {
    presentation: string;
    presentationTitle: string;
    presentationContent?: string;
    presentationAt?: string;
    activityId?: string;
  };
}

export interface TrainingUpdateFeedbackPresentationPayload {
  identity: string;
  type: ActivityType.TRAINING_UPDATE_FEEDBACK_PRESENTATION;
  data: {
    activityId: string;
    feedback: string;
  };
}

export type TrainingActionPayload =
  | TrainingPresentationPayload
  | TrainingFeedbackPayload
  | TrainingDescPayload
  | TrainingFeedbackPresentationPayload
  | TrainingUpdateFeedbackPayload
  | TrainingCreatePresentationPayload
  | TrainingUpdateFeedbackPresentationPayload;

export type PresentationCardData = Pick<EmployeeTraining, 'topicLabel'> &
  Pick<EmployeeTrainingActivity, 'presentationTitle'> & {
    presentationAt?: string;
    updatedAt?: string;
  };

export type FeedbackCardData = Pick<EmployeeTraining, 'topicLabel'> & {
  presentationTitle?: string;
  feedbackSentiment?: string;
  relatedAvatar?: string;
  relatedName?: string;
  updatedAt?: string;
};

export interface ParticipantRecipient {
  avatar?: string;
  email?: string;
  name?: string;
  title?: string;
}

export type ParticipantCardData = Pick<EmployeeTraining, 'topicLabel'> & {
  presentationTitle?: string;
  recipients?: ParticipantRecipient[];
  updatedAt?: string;
};

/**
 * Base options for training archive/unarchive messages
 */
export interface BaseTrainingArchiveMessageOptions {
  updatedAt?: string;
  isArchive?: boolean;
  fallbackMessage?: string;
}

/**
 * Options for training archive/unarchive topic message
 */
export interface TrainingArchiveTopicMessageOptions
  extends BaseTrainingArchiveMessageOptions {
  topicLabel?: string;
}

/**
 * Options for training archive/unarchive presentation message
 */
export interface TrainingArchivePresentationMessageOptions
  extends BaseTrainingArchiveMessageOptions {
  presentation?: string;
  training?: string;
}

/**
 * Unified options for training archive/unarchive messages
 */
export type TrainingArchiveMessageOptions =
  | TrainingArchiveTopicMessageOptions
  | TrainingArchivePresentationMessageOptions;
